import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { adminApi, type Tenant } from '@/lib/api';
import { Plus, ExternalLink, Building2, Search } from 'lucide-react';
import { Spinner } from '@/components/ui/Spinner';
import { toast } from '@/components/ui/toast';

const STATUS_COLOR: Record<string, { bg: string; text: string; border: string }> = {
  ACTIVE:    { bg: '#10b98120', text: '#34d399', border: '#10b98140' },
  TRIAL:     { bg: '#f59e0b20', text: '#fbbf24', border: '#f59e0b40' },
  SUSPENDED: { bg: '#ef444420', text: '#f87171', border: '#ef444440' },
  ARCHIVED:  { bg: '#47556920', text: '#64748b', border: '#47556940' },
};
const PLAN_COLOR: Record<string, { bg: string; text: string; border: string }> = {
  TRIAL:        { bg: '#47556920', text: '#64748b', border: '#47556940' },
  STARTER:      { bg: '#0ea5e920', text: '#38bdf8', border: '#0ea5e940' },
  PROFESSIONAL: { bg: '#6366f120', text: '#818cf8', border: '#6366f140' },
  ENTERPRISE:   { bg: '#8b5cf620', text: '#a78bfa', border: '#8b5cf640' },
};

type StatusFilter = 'ALL' | 'ACTIVE' | 'TRIAL' | 'SUSPENDED' | 'ARCHIVED';
type PlanFilter   = 'ALL' | 'TRIAL' | 'STARTER' | 'PROFESSIONAL' | 'ENTERPRISE';

export function TenantsPage() {
  const qc = useQueryClient();
  const [search, setSearch]             = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [planFilter, setPlanFilter]     = useState<PlanFilter>('ALL');
  const [showCreate, setShowCreate]     = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'tenants'],
    queryFn: () => adminApi.tenants.list({ limit: 200 }),
  });

  const filtered = (data?.data ?? []).filter((t) => {
    if (statusFilter !== 'ALL' && t.status !== statusFilter) return false;
    if (planFilter   !== 'ALL' && t.plan   !== planFilter)   return false;
    if (search && !t.name.toLowerCase().includes(search.toLowerCase()) && !t.slug.includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="page">

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Building2 size={22} style={{ color: '#6366f1' }} /> All Tenants
          </h1>
          <p style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>
            {data?.total ?? '—'} tenants on the platform
          </p>
        </div>
        <button
          id="new-tenant-btn"
          onClick={() => setShowCreate(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px',
            background: 'var(--color-primary)', color: '#fff', border: 'none',
            borderRadius: 9, fontSize: 13, fontWeight: 600, cursor: 'pointer',
            boxShadow: '0 2px 12px var(--color-primary-glow)',
          }}
        >
          <Plus size={15} /> New tenant
        </button>
      </div>

      {/* ── Filters ── */}
      <div style={{ display: 'flex', gap: 10, marginTop: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        {/* Search */}
        <div style={{ position: 'relative', flex: 1, minWidth: 200, maxWidth: 340 }}>
          <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#475569', pointerEvents: 'none' }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or slug…"
            style={{
              width: '100%', padding: '8px 12px 8px 34px', background: '#0f1221',
              border: '1px solid #1e2130', borderRadius: 8, color: '#e2e8f0',
              fontSize: 13, outline: 'none', boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Status filter */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {(['ALL', 'ACTIVE', 'TRIAL', 'SUSPENDED', 'ARCHIVED'] as StatusFilter[]).map((s) => (
            <button key={s} onClick={() => setStatusFilter(s)} style={{
              padding: '6px 13px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              border: '1px solid',
              borderColor: statusFilter === s ? 'var(--color-primary)' : '#1e2130',
              background: statusFilter === s ? 'var(--color-primary-dim)' : 'transparent',
              color: statusFilter === s ? 'var(--color-primary)' : '#64748b',
            }}>
              {s === 'ALL' ? 'All status' : s.charAt(0) + s.slice(1).toLowerCase()}
            </button>
          ))}
        </div>

        {/* Plan filter */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {(['ALL', 'TRIAL', 'STARTER', 'PROFESSIONAL', 'ENTERPRISE'] as PlanFilter[]).map((p) => (
            <button key={p} onClick={() => setPlanFilter(p)} style={{
              padding: '6px 13px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              border: '1px solid',
              borderColor: planFilter === p ? '#8b5cf6' : '#1e2130',
              background: planFilter === p ? '#8b5cf620' : 'transparent',
              color: planFilter === p ? '#a78bfa' : '#64748b',
            }}>
              {p === 'ALL' ? 'All plans' : p.charAt(0) + p.slice(1).toLowerCase()}
            </button>
          ))}
        </div>
      </div>

      {/* ── Loading ── */}
      {isLoading && <div style={{ padding: '40px 0', textAlign: 'center' }}><Spinner size="md" /></div>}

      {/* ── API offline state ── */}
      {!isLoading && !data && (
        <div style={{ padding: '48px 0', textAlign: 'center', color: '#475569', border: '1px dashed #1e2130', borderRadius: 12 }}>
          <p style={{ margin: 0, fontSize: 14 }}>Could not load tenants</p>
          <p style={{ margin: '6px 0 0', fontSize: 12, color: '#334155' }}>The API server may be offline — check the toast notification for details.</p>
        </div>
      )}

      {/* ── Empty state ── */}
      {!isLoading && data && filtered.length === 0 && (
        <div style={{ padding: '56px 0', textAlign: 'center', border: '1px dashed #1e2130', borderRadius: 12, color: '#475569', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Building2 size={32} style={{ opacity: 0.3, marginBottom: 12 }} />
          <p style={{ margin: 0, fontSize: 14 }}>
            {search || statusFilter !== 'ALL' || planFilter !== 'ALL'
              ? 'No tenants match your filters'
              : 'No tenants yet'}
          </p>
          {!search && statusFilter === 'ALL' && planFilter === 'ALL' && (
            <p style={{ margin: '6px 0 0', fontSize: 12, color: '#334155' }}>Create the first tenant to get started.</p>
          )}
        </div>
      )}

      {/* ── Table ── */}
      {data && filtered.length > 0 && (
        <div style={{ marginTop: 4, overflowX: 'auto', border: '1px solid #1a1f35', borderRadius: 12 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #1a1f35' }}>
                {['Tenant', 'Plan', 'Status', 'Users', 'Created', ''].map((h) => (
                  <th key={h} style={{
                    padding: '11px 16px', textAlign: 'left',
                    color: '#475569', fontWeight: 600, fontSize: 11,
                    textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <TenantRow key={t.id} tenant={t} onMutate={() => qc.invalidateQueries({ queryKey: ['admin', 'tenants'] })} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Create Drawer ── */}
      {showCreate && (
        <CreateTenantDrawer
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); qc.invalidateQueries({ queryKey: ['admin', 'tenants'] }); }}
        />
      )}
    </div>
  );
}

// ── Tenant Row ────────────────────────────────────────────────────────────────

function TenantRow({ tenant, onMutate }: { tenant: Tenant; onMutate: () => void }) {
  const statusStyle = STATUS_COLOR[tenant.status] ?? STATUS_COLOR.ARCHIVED;
  const planStyle   = PLAN_COLOR[tenant.plan]     ?? PLAN_COLOR.TRIAL;
  const isSuspended = tenant.status === 'SUSPENDED';

  const { mutate: toggleSuspend, isPending } = useMutation({
    mutationFn: () => adminApi.tenants.update(tenant.id, {
      status: isSuspended ? 'ACTIVE' : 'SUSPENDED',
    }),
    onSuccess: () => {
      toast.success(isSuspended ? 'Tenant reactivated' : 'Tenant suspended');
      onMutate();
    },
  });

  return (
    <tr style={{ borderBottom: '1px solid #12172a' }}
      className="tableRow"
    >
      {/* Name + slug */}
      <td style={{ padding: '13px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8, flexShrink: 0,
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, fontSize: 13, color: '#fff',
          }}>
            {tenant.name[0].toUpperCase()}
          </div>
          <div>
            <p style={{ margin: 0, fontWeight: 600, color: '#e2e8f0' }}>{tenant.name}</p>
            <p style={{ margin: 0, fontSize: 11, color: '#475569', fontFamily: 'monospace' }}>@{tenant.slug}</p>
          </div>
        </div>
      </td>

      {/* Plan badge */}
      <td style={{ padding: '13px 16px' }}>
        <span style={{ padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600,
          background: planStyle.bg, color: planStyle.text, border: `1px solid ${planStyle.border}`,
        }}>
          {tenant.plan}
        </span>
      </td>

      {/* Status badge */}
      <td style={{ padding: '13px 16px' }}>
        <span style={{ padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600,
          background: statusStyle.bg, color: statusStyle.text, border: `1px solid ${statusStyle.border}`,
        }}>
          {tenant.status}
        </span>
      </td>

      {/* User count */}
      <td style={{ padding: '13px 16px', color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>
        {tenant.userCount ?? '—'}
      </td>

      {/* Date */}
      <td style={{ padding: '13px 16px', color: '#64748b', fontSize: 12, whiteSpace: 'nowrap' }}>
        {new Date(tenant.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
      </td>

      {/* Actions */}
      <td style={{ padding: '13px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Link
            to="/tenant/$tenantId"
            params={{ tenantId: tenant.id }}
            id={`tenant-row-${tenant.slug}`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '5px 11px', border: '1px solid #2d3148', borderRadius: 7,
              color: '#94a3b8', fontSize: 12, fontWeight: 500, textDecoration: 'none',
            }}
          >
            <ExternalLink size={12} /> View
          </Link>
          <button
            onClick={() => toggleSuspend()}
            disabled={isPending || tenant.status === 'ARCHIVED'}
            style={{
              padding: '5px 11px', borderRadius: 7, fontSize: 12, fontWeight: 500,
              cursor: tenant.status === 'ARCHIVED' ? 'default' : 'pointer',
              border: '1px solid',
              borderColor: isSuspended ? '#10b98140' : '#ef444440',
              background: 'transparent',
              color: isSuspended ? '#34d399' : tenant.status === 'ARCHIVED' ? '#334155' : '#f87171',
              opacity: tenant.status === 'ARCHIVED' ? 0.4 : 1,
            }}
          >
            {isPending ? <Spinner size="sm" /> : isSuspended ? 'Activate' : 'Suspend'}
          </button>
        </div>
      </td>
    </tr>
  );
}

// ── Create Drawer ─────────────────────────────────────────────────────────────

function CreateTenantDrawer({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName]     = useState('');
  const [slug, setSlug]     = useState('');
  const [plan, setPlan]     = useState<'TRIAL' | 'STARTER' | 'PROFESSIONAL' | 'ENTERPRISE'>('TRIAL');
  const [slugTouched, setSlugTouched] = useState(false);

  const derivedSlug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 50);
  const effectiveSlug = slugTouched ? slug : derivedSlug;

  const { mutate, isPending } = useMutation({
    mutationFn: () => adminApi.tenants.create({ name: name.trim(), slug: effectiveSlug, plan }),
    onSuccess: (t: Tenant) => {
      toast.success(`Tenant "${t.name}" created`);
      onCreated();
    },
  });

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', zIndex: 40 }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: '100%', maxWidth: 440,
        background: '#0d1022', borderLeft: '1px solid #1e2130', zIndex: 50,
        padding: '28px 24px', overflowY: 'auto',
        boxShadow: '-16px 0 60px rgba(0,0,0,0.6)',
        animation: 'drawerIn 0.22s cubic-bezier(0.34,1.2,0.64,1)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#e2e8f0' }}>New Tenant</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: 20, lineHeight: 1 }}>×</button>
        </div>

        <div style={{ marginBottom: 18 }}>
          <label style={labelStyle}>Company name <span style={{ color: '#f87171' }}>*</span></label>
          <input id="tenant-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Corporation" autoFocus style={inputStyle} />
        </div>

        <div style={{ marginBottom: 18 }}>
          <label style={labelStyle}>Slug <span style={{ color: '#f87171' }}>*</span></label>
          <input
            id="tenant-slug"
            value={effectiveSlug}
            onChange={(e) => { setSlug(e.target.value); setSlugTouched(true); }}
            placeholder="acme"
            style={inputStyle}
          />
          <p style={{ margin: '5px 0 0', fontSize: 11, color: '#475569' }}>
            Immutable after creation. Lowercase letters, numbers, hyphens only.
          </p>
        </div>

        <div style={{ marginBottom: 24 }}>
          <label style={labelStyle}>Plan</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {(['TRIAL', 'STARTER', 'PROFESSIONAL', 'ENTERPRISE'] as const).map((p) => {
              const c = PLAN_COLOR[p];
              return (
                <button key={p} onClick={() => setPlan(p)} style={{
                  padding: '8px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                  cursor: 'pointer', border: '1px solid',
                  borderColor: plan === p ? c.border : '#1e2130',
                  background: plan === p ? c.bg : 'transparent',
                  color: plan === p ? c.text : '#64748b',
                }}>
                  {p.charAt(0) + p.slice(1).toLowerCase()}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid #1e2130', borderRadius: 9, color: '#64748b', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Cancel
          </button>
          <button
            id="create-tenant-submit"
            onClick={() => mutate()}
            disabled={!name.trim() || !effectiveSlug || isPending}
            style={{ flex: 2, padding: '10px', background: 'var(--color-primary)', border: 'none', borderRadius: 9, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: (!name.trim() || !effectiveSlug) ? 0.5 : 1 }}
          >
            {isPending ? <Spinner size="sm" /> : 'Create tenant'}
          </button>
        </div>
      </div>
      <style>{`
        @keyframes drawerIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .tableRow:hover { background: #0f1221 !important; }
      `}</style>
    </>
  );
}

const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: '#94a3b8', marginBottom: 6 };
const inputStyle: React.CSSProperties = { width: '100%', padding: '9px 12px', background: '#0f1221', border: '1px solid #1e2130', borderRadius: 8, color: '#e2e8f0', fontSize: 13, outline: 'none', boxSizing: 'border-box' };
